/**
 * Print templates (03-modules.md §5 "The server renders a print job to an ESC/POS
 * document model using templates"): KOT, bill, test page and table QR, each a pure
 * function from the facts to a `PrintDoc` (packages/contracts print.ts). The
 * outlet's template (`print_template.body`) adds header/footer lines and a few
 * switches; layout is fixed here. Money is integer paise, quantities milli-units.
 *
 * TODO(legacy): compare the KOT and bill layouts with the Laravel printers
 * (`Printer*`, `printraw.js`) once that code is available.
 */
import {
  DEFAULT_PRINT_TEMPLATE_BODY,
  PAPER_CHARS,
  PRINT_DOC_VERSION,
  type PaperWidthMm,
  type PrintBlock,
  type PrintDoc,
  type PrintTemplateBody,
} from '@qrkey/contracts';
import type { KotContentLine, KotKindName } from './kots.js';

// ---------------------------------------------------------------------------
// Formatting

/** 1000 → "1", 1500 → "1.5", 250 → "0.25". */
export function formatQty(milli: number): string {
  const sign = milli < 0 ? '-' : '';
  const abs = Math.abs(Math.trunc(milli));
  const whole = Math.floor(abs / 1000);
  const frac = String(abs % 1000)
    .padStart(3, '0')
    .replace(/0+$/, '');
  return `${sign}${whole}${frac ? `.${frac}` : ''}`;
}

/** Paise as rupees with Indian digit grouping: 12345678n → "1,23,456.78". */
export function formatRupees(paise: bigint): string {
  const negative = paise < 0n;
  const abs = negative ? -paise : paise;
  const rupees = (abs / 100n).toString();
  const cents = (abs % 100n).toString().padStart(2, '0');
  let grouped = rupees;
  if (rupees.length > 3) {
    const head = rupees.slice(0, -3);
    const tail = rupees.slice(-3);
    grouped = `${head.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${tail}`;
  }
  return `${negative ? '-' : ''}${grouped}.${cents}`;
}

/** `19-10-2026 13:05` in the outlet's timezone. */
export function formatOutletTime(at: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(at);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('day')}-${get('month')}-${get('year')} ${get('hour')}:${get('minute')}`;
}

/** Basis points as a percent label: 250 → "2.5%", 900 → "9%". */
export function formatRate(bp: number): string {
  const whole = Math.floor(bp / 100);
  const frac = String(bp % 100)
    .padStart(2, '0')
    .replace(/0+$/, '');
  return `${whole}${frac ? `.${frac}` : ''}%`;
}

const ORDER_TYPE_LABELS: Readonly<Record<string, string>> = {
  dine_in: 'DINE IN',
  takeaway: 'TAKEAWAY',
  delivery: 'DELIVERY',
  aggregator: 'ONLINE',
};

export function orderTypeLabel(type: string): string {
  return ORDER_TYPE_LABELS[type] ?? type.toUpperCase();
}

// ---------------------------------------------------------------------------
// Building blocks

const text = (value: string, style: Omit<Extract<PrintBlock, { t: 'text' }>, 't' | 'text'> = {}) =>
  ({ t: 'text', text: value, ...style }) as PrintBlock;
const rule = (char: '-' | '=' | '*' | '.' = '-'): PrintBlock => ({ t: 'rule', char });
const feed = (lines = 1): PrintBlock => ({ t: 'feed', lines });
const cut = (): PrintBlock => ({ t: 'cut' });

function headerFooter(lines: readonly string[]): PrintBlock[] {
  return lines.filter((l) => l.trim() !== '').map((l) => text(l, { align: 'center' }));
}

function watermarkBlocks(watermark: string | null): PrintBlock[] {
  if (watermark === null) return [];
  return [text(`*** ${watermark} ***`, { align: 'center', bold: true, width: 2, height: 2 })];
}

function doc(
  title: string,
  paperMm: PaperWidthMm,
  copies: number,
  watermark: string | null,
  blocks: PrintBlock[],
): PrintDoc {
  return {
    version: PRINT_DOC_VERSION,
    paperMm,
    title: title.slice(0, 120),
    watermark,
    copies: Math.min(5, Math.max(1, copies)),
    blocks,
  };
}

function bodyOf(template: Partial<PrintTemplateBody> | undefined): PrintTemplateBody {
  return { ...DEFAULT_PRINT_TEMPLATE_BODY, ...(template ?? {}) };
}

// ---------------------------------------------------------------------------
// KOT

export interface KotPrintData {
  readonly outletName: string;
  readonly stationName: string;
  readonly kotNo: number;
  readonly kind: KotKindName;
  /** A section copy. */
  readonly copy: boolean;
  readonly orderNo: number;
  readonly orderType: string;
  readonly tableNames: readonly string[];
  /** Pre-formatted (formatOutletTime). */
  readonly at: string;
  /** Where the order came from (kotSourceFacts): `Swiggy`, `Takeaway`, `QR`; null at the till. */
  readonly sourceLabel?: string | null;
  /** The order source's packaging note (06-flows.md F5 step 4); printed boxed, bold. */
  readonly packagingNote?: string | null;
  /** Who sent it (kotSentBy): `Ravi @ Captain 2`. */
  readonly sentBy?: string | null;
  /** A void KOT: why the items were taken back. */
  readonly voidReason?: string | null;
  readonly lines: readonly KotContentLine[];
  readonly paperMm: PaperWidthMm;
  readonly copies: number;
  /** `DUPLICATE` on a reprint. */
  readonly watermark: string | null;
}

/** `TAKEAWAY / SWIGGY`; the source alone when it says the same as the type. */
function kotTypeLine(orderType: string, sourceLabel: string | null): string {
  const type = orderTypeLabel(orderType);
  const source = sourceLabel?.trim().toUpperCase() ?? '';
  const same = source.replace(/[^A-Z0-9]/g, '') === type.replace(/[^A-Z0-9]/g, '');
  return source === '' || same ? type : `${type} / ${source}`;
}

const KOT_TITLES: Readonly<Record<KotKindName, string>> = {
  new: 'KOT',
  incremental: 'KOT (ADD-ON)',
  void: 'VOID KOT',
  reprint: 'KOT',
};

export function renderKotDoc(data: KotPrintData, template?: Partial<PrintTemplateBody>): PrintDoc {
  const t = bodyOf(template);
  const blocks: PrintBlock[] = [
    ...watermarkBlocks(data.watermark),
    ...headerFooter(t.header),
    text(KOT_TITLES[data.kind], {
      align: 'center',
      bold: true,
      width: 2,
      height: 2,
      invert: data.kind === 'void',
    }),
    text(`${data.stationName}${data.copy ? ' (COPY)' : ''}`, { align: 'center', bold: true }),
    rule(),
    {
      t: 'row',
      cells: [
        { text: `KOT #${data.kotNo}`, bold: true },
        { text: `Order #${data.orderNo}`, align: 'right' },
      ],
    },
    {
      t: 'row',
      cells: [
        { text: kotTypeLine(data.orderType, data.sourceLabel ?? null) },
        {
          text: data.tableNames.length > 0 ? `Table ${data.tableNames.join(', ')}` : '',
          align: 'right',
          bold: true,
        },
      ],
    },
    text(data.at),
    ...(data.sentBy ? [text(`By: ${data.sentBy}`)] : []),
    ...(data.kind === 'void' && data.voidReason
      ? [text(`Reason: ${data.voidReason}`, { bold: true })]
      : []),
    ...(data.packagingNote
      ? [rule('*'), text(`PACKING: ${data.packagingNote}`, { bold: true }), rule('*')]
      : []),
    rule(),
  ];
  const full = data.lines.some((l) => !l.isNew);
  const itemStyle = t.largeItems ? { height: 2 as const } : {};
  for (const line of data.lines) {
    const mark = data.kind === 'void' ? '-' : full && line.isNew ? '+' : '';
    const seat = line.seat !== null ? ` [S${line.seat}]` : '';
    blocks.push({
      t: 'row',
      bold: line.isNew || data.kind === 'void',
      ...itemStyle,
      cells: [
        { text: `${mark}${formatQty(line.qtyMilli)}`, flex: 1 },
        { text: `${line.name}${seat}`, flex: 6 },
      ],
    });
    if (t.showModifiers) {
      for (const m of line.modifiers) {
        const qty = m.qtyMilli !== 1000 ? ` x${formatQty(m.qtyMilli)}` : '';
        blocks.push({
          t: 'row',
          cells: [
            { text: '', flex: 1 },
            { text: `  + ${m.name}${qty}`, flex: 6 },
          ],
        });
      }
    }
    if (t.showNotes && line.note) {
      blocks.push({
        t: 'row',
        cells: [
          { text: '', flex: 1 },
          { text: `  ** ${line.note}`, flex: 6, bold: true },
        ],
      });
    }
  }
  const count = data.lines.reduce((n, l) => n + (l.isNew || data.kind === 'void' ? 1 : 0), 0);
  blocks.push(rule(), text(`Items: ${count}`), ...headerFooter(t.footer));
  blocks.push(...watermarkBlocks(data.watermark), feed(2), cut());
  return doc(
    `KOT ${data.kotNo} ${data.stationName}`,
    data.paperMm,
    data.copies,
    data.watermark,
    blocks,
  );
}

// ---------------------------------------------------------------------------
// Bill (GST invoice layout as far as the facts go)

export interface BillPrintLine {
  readonly name: string;
  readonly qtyMilli: number;
  /** Per unit, with its modifiers. */
  readonly unitPricePaise: bigint;
  /** qty × unit price (before order discounts and taxes on exclusive items). */
  readonly amountPaise: bigint;
  readonly complimentary: boolean;
  readonly modifiers: readonly string[];
  /**
   * HSN/SAC code printed under the item on a tax invoice: the invoice's (accounting)
   * once issued, else the item's (menu `hsn_sac`); null prints none.
   */
  readonly hsn?: string | null;
}

/** The two-digit GST state code a GSTIN starts with (`29ABCDE1234F1Z5` → `29`), or null. */
export function gstinStateCode(gstin: string | null | undefined): string | null {
  const m = /^(\d{2})[0-9A-Z]{13}$/.exec((gstin ?? '').trim().toUpperCase());
  return m?.[1] ?? null;
}

export interface BillPrintData {
  readonly outlet: {
    readonly name: string;
    readonly addressLines: readonly string[];
    readonly gstin: string | null;
    readonly fssaiNo: string | null;
  };
  readonly orderNo: number;
  readonly orderType: string;
  readonly tableNames: readonly string[];
  readonly covers: number;
  /**
   * Pre-formatted (formatOutletTime): the bill's time, or a tax invoice's date and time
   * of issue (rule 46(d)), never the time a reprint is made.
   */
  readonly at: string;
  /** A tax invoice printed (or reprinted) later: when, pre-formatted; null otherwise. */
  readonly printedAt?: string | null;
  /**
   * The GST invoice number, once accounting issues it at settlement (CLAUDE.md rule
   * 7). Null before: the bill prints without one.
   */
  readonly invoiceNo: string | null;
  readonly customerName: string | null;
  /**
   * A B2B tax invoice: the buyer's legal name and GSTIN (accounting's
   * `invoice.buyer_gstin`). Null for a B2C bill.
   */
  readonly buyer?: {
    readonly name: string | null;
    readonly gstin: string;
    /** The recipient's address (rule 46(e)). */
    readonly address?: string | null;
  } | null;
  /**
   * Place of supply: a restaurant's supply is where it is served, so the outlet's state
   * and its GST state code (gstinStateCode of the outlet's GSTIN).
   */
  readonly placeOfSupply?: { readonly state: string; readonly code: string | null } | null;
  readonly lines: readonly BillPrintLine[];
  readonly subtotalPaise: bigint;
  readonly discounts: readonly { name: string; amountPaise: bigint }[];
  readonly charges: readonly { name: string; amountPaise: bigint }[];
  /** Per component and rate; `taxablePaise` (the taxable value) prints the GST summary. */
  readonly taxes: readonly {
    component: string;
    rateBp: number;
    taxPaise: bigint;
    taxablePaise?: bigint;
  }[];
  readonly roundOffPaise: bigint;
  readonly totalPaise: bigint;
  readonly paidPaise: bigint;
  readonly paperMm: PaperWidthMm;
  readonly copies: number;
  /** `REPRINT` on a reprint. */
  readonly watermark: string | null;
}

const money = (p: bigint) => formatRupees(p);

export function renderBillDoc(
  data: BillPrintData,
  template?: Partial<PrintTemplateBody>,
): PrintDoc {
  const t = bodyOf(template);
  const o = data.outlet;
  const blocks: PrintBlock[] = [
    ...watermarkBlocks(data.watermark),
    text(o.name, { align: 'center', bold: true, width: 2, height: 2 }),
    ...o.addressLines.map((l) => text(l, { align: 'center' })),
    ...(o.gstin ? [text(`GSTIN: ${o.gstin}`, { align: 'center' })] : []),
    ...(o.fssaiNo ? [text(`FSSAI: ${o.fssaiNo}`, { align: 'center' })] : []),
    ...headerFooter(t.header),
    rule(),
    text(data.invoiceNo !== null ? 'TAX INVOICE' : 'BILL', { align: 'center', bold: true }),
    {
      t: 'row',
      cells: [
        { text: data.invoiceNo !== null ? `Inv: ${data.invoiceNo}` : `Bill #${data.orderNo}` },
        { text: data.at, align: 'right' },
      ],
    },
    {
      t: 'row',
      cells: [
        { text: orderTypeLabel(data.orderType) },
        {
          text: [
            data.tableNames.length > 0 ? `Table ${data.tableNames.join(', ')}` : '',
            data.covers > 0 ? `Covers ${data.covers}` : '',
          ]
            .filter(Boolean)
            .join('  '),
          align: 'right',
        },
      ],
    },
    ...(data.invoiceNo !== null ? [text(`Order #${data.orderNo}`)] : []),
    ...(data.printedAt ? [text(`Printed: ${data.printedAt}`)] : []),
    ...(data.customerName ? [text(`Customer: ${data.customerName}`)] : []),
    ...(data.buyer
      ? [
          ...(data.buyer.name ? [text(`Buyer: ${data.buyer.name}`)] : []),
          ...(data.buyer.address ? [text(`Address: ${data.buyer.address}`)] : []),
          text(`Buyer GSTIN: ${data.buyer.gstin}`),
        ]
      : []),
    ...(data.placeOfSupply
      ? [
          text(
            `Place of supply: ${data.placeOfSupply.state}${
              data.placeOfSupply.code ? ` (${data.placeOfSupply.code})` : ''
            }`,
          ),
        ]
      : []),
    rule(),
    {
      t: 'row',
      bold: true,
      cells: [
        { text: 'Item', flex: 5 },
        { text: 'Qty', flex: 1, align: 'right' },
        { text: 'Rate', flex: 2, align: 'right' },
        { text: 'Amt', flex: 2, align: 'right' },
      ],
    },
    rule(),
  ];
  for (const l of data.lines) {
    blocks.push({
      t: 'row',
      cells: [
        { text: l.complimentary ? `${l.name} (comp)` : l.name, flex: 5 },
        { text: formatQty(l.qtyMilli), flex: 1, align: 'right' },
        { text: money(l.unitPricePaise), flex: 2, align: 'right' },
        { text: money(l.complimentary ? 0n : l.amountPaise), flex: 2, align: 'right' },
      ],
    });
    if (t.showModifiers) {
      for (const m of l.modifiers) blocks.push(text(`  + ${m}`));
    }
    if (l.hsn) blocks.push(text(`  HSN/SAC: ${l.hsn}`));
  }
  const total = (label: string, amount: bigint, bold = false): PrintBlock => ({
    t: 'row',
    bold,
    cells: [
      { text: label, flex: 3 },
      { text: money(amount), flex: 2, align: 'right' },
    ],
  });
  blocks.push(rule(), total('Subtotal', data.subtotalPaise));
  for (const d of data.discounts) blocks.push(total(`Discount: ${d.name}`, -d.amountPaise));
  for (const c of data.charges) blocks.push(total(c.name, c.amountPaise));
  for (const x of data.taxes) {
    blocks.push(total(`${x.component.toUpperCase()} @ ${formatRate(x.rateBp)}`, x.taxPaise));
  }
  if (data.roundOffPaise !== 0n) blocks.push(total('Round off', data.roundOffPaise));
  blocks.push(rule('='), {
    t: 'row',
    bold: true,
    height: 2,
    cells: [
      { text: 'TOTAL', flex: 3 },
      { text: `Rs ${money(data.totalPaise)}`, flex: 3, align: 'right' },
    ],
  });
  if (data.paidPaise > 0n) {
    blocks.push(total('Paid', data.paidPaise));
    if (data.totalPaise - data.paidPaise > 0n) {
      blocks.push(total('Due', data.totalPaise - data.paidPaise, true));
    }
  }
  const summary = data.taxes.filter((x) => x.taxablePaise !== undefined);
  if (summary.length > 0) {
    blocks.push(rule(), text('GST summary', { bold: true }), {
      t: 'row',
      bold: true,
      cells: [
        { text: 'Tax', flex: 3 },
        { text: 'Taxable', flex: 3, align: 'right' },
        { text: 'Amount', flex: 2, align: 'right' },
      ],
    });
    for (const x of summary) {
      blocks.push({
        t: 'row',
        cells: [
          { text: `${x.component.toUpperCase()} ${formatRate(x.rateBp)}`, flex: 3 },
          { text: money(x.taxablePaise ?? 0n), flex: 3, align: 'right' },
          { text: money(x.taxPaise), flex: 2, align: 'right' },
        ],
      });
    }
  }
  blocks.push(rule('='), ...headerFooter(t.footer));
  if (t.footer.length === 0) blocks.push(text('Thank you! Visit again.', { align: 'center' }));
  blocks.push(...watermarkBlocks(data.watermark), feed(2), cut());
  return doc(`Bill ${data.orderNo}`, data.paperMm, data.copies, data.watermark, blocks);
}

// ---------------------------------------------------------------------------
// Test page

export interface TestPrintData {
  readonly outletName: string;
  readonly printerName: string;
  readonly connection: string;
  readonly address: string | null;
  readonly paperMm: PaperWidthMm;
  /** Pre-formatted (formatOutletTime). */
  readonly at: string;
}

export function renderTestDoc(
  data: TestPrintData,
  template?: Partial<PrintTemplateBody>,
): PrintDoc {
  const t = bodyOf(template);
  const width = PAPER_CHARS[data.paperMm];
  const ruler = '1234567890'.repeat(Math.ceil(width / 10)).slice(0, width);
  const blocks: PrintBlock[] = [
    ...headerFooter(t.header),
    text('TEST PRINT', { align: 'center', bold: true, width: 2, height: 2 }),
    text(data.outletName, { align: 'center' }),
    rule(),
    text(`Printer: ${data.printerName}`),
    text(`Connection: ${data.connection}${data.address ? ` (${data.address})` : ''}`),
    text(`Paper: ${data.paperMm} mm, ${width} characters per line`),
    text(`Time: ${data.at}`),
    rule(),
    text(ruler),
    text('Left'),
    text('Centre', { align: 'center' }),
    text('Right', { align: 'right' }),
    text('Bold', { bold: true }),
    text('Underline', { underline: true }),
    text('Double size', { width: 2, height: 2 }),
    text('Inverted', { invert: true }),
    { t: 'row', cells: [{ text: 'Item' }, { text: 'Rs 1,234.50', align: 'right' }] },
    text('Rupee sign: ₹ 100'),
    { t: 'qr', data: 'https://qrkey.in', size: 4, align: 'center' },
    ...headerFooter(t.footer),
    text('If you can read this, printing works.', { align: 'center' }),
    feed(2),
    cut(),
  ];
  return doc(`Test page ${data.printerName}`, data.paperMm, 1, null, blocks);
}

// ---------------------------------------------------------------------------
// Table QR stand

export interface TableQrPrintData {
  readonly outletName: string;
  readonly tableName: string;
  readonly sectionName: string;
  /** The signed table URL the QR encodes (org's table QR). */
  readonly url: string;
  readonly paperMm: PaperWidthMm;
}

export function renderTableQrDoc(
  data: TableQrPrintData,
  template?: Partial<PrintTemplateBody>,
): PrintDoc {
  const t = bodyOf(template);
  const blocks: PrintBlock[] = [
    ...headerFooter(t.header),
    text(data.outletName, { align: 'center', bold: true, width: 2, height: 2 }),
    feed(),
    text(`Table ${data.tableName}`, { align: 'center', bold: true, width: 2, height: 2 }),
    text(data.sectionName, { align: 'center' }),
    feed(),
    { t: 'qr', data: data.url, size: data.paperMm === 80 ? 8 : 6, align: 'center' },
    feed(),
    text('Scan to see the menu and order', { align: 'center', bold: true }),
    ...headerFooter(t.footer),
    feed(2),
    cut(),
  ];
  return doc(`QR ${data.tableName}`, data.paperMm, 1, null, blocks);
}
